import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { VisitFormRule, VisitFormSchema } from "@factory/form/form.rule";
import { useFormDefaultVisit } from "@factory/form/form-default-visit.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateVisit,
  useQyGetVisitById,
  useQyDeleteVisit,
  useQyAttachFileVisit,
} from "@factory/query/visit.query";
import { GetResponseVisitModel } from "@factory/model/query-model-visit";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { useQyGetBilling } from "@factory/query/billing.query";
import {
  canInitiatePayment,
  getPaymentBlockedReason,
} from "@core/utility/billing-financials";
import DashboardCard from "@shared/ui/dashboard-card/dashboard-card";
import {
  FaCoins,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaWallet,
} from "react-icons/fa";

export function useEditVisit() {
  // LOCAL DECLARATION
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultVisit();
  const navigate = useNavigate();
  const { mapUpdateVisit, mapDeleteVisit } = useFormApiMapper();
  const { showError, showSuccess, showInfo } = useNotificationContext();
  const { visitId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const location = useLocation();

  // QUERY DECLARATION
  // API GETBYID Visit
  const handleGetApiSuccess = (data: GetResponseVisitModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("patient", responseData?.patient || "");
    const patientName = `${responseData?.patient_first_name} ${responseData?.patient_last_name}`;
    setValue("patientName", patientName);
    setValue("contract", responseData?.contract || "");
    setValue("billing", responseData?.billing || "");
    if (responseData?.date) {
      setValue("date", formatDateForInput(responseData?.date));
    }
    setValue("time", responseData?.time || "");
    setValue("description", responseData?.description || "");
    setValue("prescription", responseData?.prescription || "");
    setValue("person", responseData?.person || "");
    const doctorName = `${responseData?.person_first_name} ${responseData?.person_last_name}`;
    setValue("doctorName", doctorName);
  };
  const {
    data: visitResponse,
    isLoading,
    isError: visitError,
  } = useQyGetVisitById(visitId || "", handleGetApiSuccess);
  const visitData = visitResponse?.data?.[0];
  const contractId = visitData?.contract ?? "";

  const { data: billingResponse } = useQyGetBilling(
    "",
    9999,
    0,
    undefined,
    {
      contract: contractId || "",
    },
    true
  );

  const billingData = billingResponse?.data || [];

  // API VIEW ASSET
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // API UPDATE Visit
  const handleUpdateApiSuccess = () => {
    showSuccess("Visit updated successfully");
  };
  const { mutate: updateVisit, isLoading: isEditLoading } = useQyUpdateVisit(
    handleUpdateApiSuccess
  );
  const { mutateAsync: uploadAttachments } = useQyAttachFileVisit();

  // API DELETE Visit
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Visit deleted successfully");
  };
  const { mutate: deleteVisit } = useQyDeleteVisit(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS

  const formMethod = useForm<VisitFormSchema>({
    defaultValues: getDefault(visitData),
    resolver: zodResolver(VisitFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = async (form: VisitFormSchema) => {
    if (!visitData || !visitData.code) {
      throw new Error("No visit data");
    }

    const formData = mapUpdateVisit(form, visitData.code);
    updateVisit(formData);

    if (form.photos && (form.photos as FileList).length > 0) {
      try {
        await uploadAttachments({
          attachment: {
            visit: visitData.code,
          },
          file: [...(form.photos || [])],
        });
        showSuccess("Attachments uploaded successfully");
      } catch (error) {
        showError("Visit updated but failed to upload attachments");
      }
    }
  };
  const handleValidateError = (err: FieldErrors<VisitFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!visitData || !visitData.code) {
      throw new Error("No visit data found");
    }

    const form = mapDeleteVisit(visitData.code);
    deleteVisit(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const billingId = visitData?.billing ?? "";
  // Prefer the matching row from view_billing (already fetched above) over
  // visitData's own balance/status - it's the same canonical source every
  // other billing screen reads from, rather than a second, independent copy.
  const matchingBilling = billingData.find((b) => b.code === billingId);
  const sessionBalance = matchingBilling?.balance ?? visitData?.balance ?? 0;
  const sessionStatus = matchingBilling?.status;

  const hasPatient = location.pathname.includes("/patient/");
  const hasContract = location.pathname.includes("/contract/");
  const hasVisit = location.pathname.includes("/visit/");
  const isPatientContractVisitRoute = hasPatient && hasContract && hasVisit;

  const targetPath =
    isPatientContractVisitRoute || !hasPatient
      ? `../../pay-session?billingId=${billingId}`
      : `../../contract/${contractId}/pay-session?billingId=${billingId}`;

  const canPaySession = canInitiatePayment(sessionStatus, sessionBalance);
  const handlePaySession = () => {
    if (isLoading || !visitData) return;
    if (!visitData?.session) {
      showInfo("This visit is not linked to a billing session.");
      return;
    }
    const blockedReason = getPaymentBlockedReason(sessionStatus, sessionBalance);
    if (blockedReason) {
      showInfo(blockedReason);
      return;
    }

    navigate(targetPath, {
      state: { balance: sessionBalance },
    });
  };

  const summary = billingData?.length > 0 && (
    <div className="flex flex-wrap gap-4 ml-5 mr-5 mt-5 mb-0">
      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Contract Amount",
            value: currencyFormat(billingData[0].contract_amount, "PHP"),
            icon: <FaMoneyBillWave className="text-gray-600 text-2xl" />,
            iconClass: "bg-gray-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Amount Paid",
            value: currencyFormat(billingData[0].total_amount_paid, "PHP"),
            icon: <FaCoins className="text-green-600 text-2xl" />,
            iconClass: "bg-green-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Total Balance",
            value: currencyFormat(billingData[0].total_balance, "PHP"),
            icon: <FaWallet className="text-red-500 text-2xl" />,
            iconClass: "bg-red-100",
            description: "",
          }}
        />
      </div>

      <div className="flex-1 min-w-[250px]">
        <DashboardCard
          model={{
            title: "Session Balance",
            value: currencyFormat(sessionBalance, "PHP"),
            icon: <FaHandHoldingUsd className="text-red-500 text-2xl" />,
            iconClass: "bg-red-100",
            description: "",
          }}
        />
      </div>
    </div>
  );

  return {
    visitId,
    visitData,
    isLoading,
    visitError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    handlePaySession,
    canPaySession,
    summary,
  };
}
